import 'temporal-polyfill/global';
import {HDate, Locale} from '@hebcal/hdate';
import {CalOptions} from './CalOptions.js';
import {flags} from './event.js';
import {getHolidaysOnDate} from './holidays.js';
import {Location} from './location.js';
import {Molad} from './molad.js';
import {reformatTimeStr} from './reformatTimeStr.js';
import {TimedEvent} from './TimedEvent.js';
import {Zmanim} from './zmanim.js';
import './locale.js'; // Adds Hebrew and Ashkenazic translations

const LATEST_KIDDUSH_LEVANA = 'Latest Kiddush Levana';

/**
 * The latest time to recite _Kiddush Levana_ (Sanctification of the Moon),
 * calculated as halfway between one _molad_ and the next, according to
 * the [Maharil](https://en.wikipedia.org/wiki/Yaakov_ben_Moshe_Levi_Moelin)
 * (14 days, 18 hours, 22 minutes and 1.666 seconds after the _molad_).
 *
 * Generated by {@link calendar} when `options.kiddushLevanaMaharil` is
 * `true` and `options.location` is provided:
 * - When the time falls during the day (between _alot hashachar_ 16.1° and
 *   _tzeit_ 8.5°), it is moved back to _alot hashachar_ that morning, the
 *   end of the last night on which Kiddush Levana can be said.
 * - When the time falls during Shabbat or a Yom Tov, it is moved back to
 *   regular candle-lighting time before that Shabbat or Yom Tov.
 * - The time is rounded down to the minute, never up.
 *
 * The {@link memo} lists the unadjusted earliest (3 and 7 days after the
 * _molad_) and latest (Maharil and 15 days after the _molad_) times.
 */
export class KiddushLevanaEvent extends TimedEvent {
  /** The molad of the month this event belongs to */
  readonly molad: Molad;
  /**
   * Normally created by {@link calendar} rather than directly.
   * @param date Hebrew date the event occurs
   * @param eventTime latest time for Kiddush Levana, possibly adjusted
   * @param location geographic location used to format the time
   * @param molad the molad of the month
   * @param options optional; `locale` and `hour12` affect the formatted times
   */
  constructor(
    date: HDate,
    eventTime: Date,
    location: Location,
    molad: Molad,
    options?: CalOptions
  ) {
    super(
      date,
      LATEST_KIDDUSH_LEVANA,
      flags.KIDDUSH_LEVANA,
      eventTime,
      location,
      undefined,
      options
    );
    this.molad = molad;
    this.memo = makeMemo(molad, location, options);
  }

  getEmoji(): string {
    return '🌔';
  }

  getCategories(): string[] {
    return ['zmanim', 'kiddushLevana'];
  }
}

function toDate(zdt: Temporal.ZonedDateTime): Date {
  return new Date(zdt.epochMilliseconds);
}

function toZdt(dt: Date, tzid: string): Temporal.ZonedDateTime | null {
  const millis = dt.getTime();
  if (isNaN(millis)) {
    return null;
  }
  return Temporal.Instant.fromEpochMilliseconds(millis).toZonedDateTimeISO(tzid);
}

function floorMinute(zdt: Temporal.ZonedDateTime): Temporal.ZonedDateTime {
  return zdt.round({smallestUnit: 'minute', roundingMode: 'floor'});
}

function ceilMinute(zdt: Temporal.ZonedDateTime): Temporal.ZonedDateTime {
  return zdt.round({smallestUnit: 'minute', roundingMode: 'ceil'});
}

function makeMemo(molad: Molad, location: Location, options?: CalOptions): string {
  const tzid = location.getTzid();
  const locale = options?.locale;
  const times: [string, Temporal.ZonedDateTime][] = [
    [
      'Earliest Kiddush Levana (3 days)',
      ceilMinute(molad.getTchilasZmanKidushLevana3Days()),
    ],
    [
      'Earliest Kiddush Levana (7 days)',
      ceilMinute(molad.getTchilasZmanKidushLevana7Days()),
    ],
    [
      'Latest Kiddush Levana (Maharil)',
      floorMinute(molad.getSofZmanKidushLevanaBetweenMoldos()),
    ],
    [
      'Latest Kiddush Levana (15 days)',
      floorMinute(molad.getSofZmanKidushLevana15Days()),
    ],
  ];
  const dateFormat = new Intl.DateTimeFormat(intlLocale(locale), {
    timeZone: tzid,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const timeFormat = location.getTimeFormatter();
  const opts = {...options, location};
  return times
    .map(([label, zdt]) => {
      const dt = toDate(zdt);
      const timeStr = reformatTimeStr(
        Zmanim.formatTime(dt, timeFormat),
        'pm',
        opts
      );
      return `${Locale.gettext(label, locale)}: ${dateFormat.format(dt)}, ${timeStr}`;
    })
    .join('\n');
}

function intlLocale(locale?: string): string {
  if (!locale || locale === 'en' || locale === 'ashkenazi') {
    return 'en-US';
  }
  if (Locale.isHebrewLocale(locale)) {
    return 'he-IL';
  }
  try {
    return Intl.DateTimeFormat.supportedLocalesOf(locale)[0] ?? 'en-US';
  } catch {
    return 'en-US';
  }
}

function isShabbatOrChag(hd: HDate, il: boolean): boolean {
  if (hd.getDay() === 6) {
    return true;
  }
  const events = getHolidaysOnDate(hd, il) ?? [];
  return events.some(ev => ev.hasFlag('CHAG'));
}

/**
 * Makes the "Latest Kiddush Levana" event for one Hebrew month, or
 * `undefined` if it can't be calculated (e.g. no sunrise or sunset).
 * The returned event's date may be earlier than the date of the
 * actual latest time, when that time falls on Shabbat or a Yom Tov.
 * @internal
 */
export function makeKiddushLevanaEvent(
  hyear: number,
  hmonth: number,
  options: CalOptions
): KiddushLevanaEvent | undefined {
  const location = options.location as Location;
  const useElevation = Boolean(options.useElevation);
  const il = Boolean(options.il);
  const tzid = location.getTzid();
  // Find the civil date on which the Maharil's time falls.
  // Zmanim only computes this zman between the 11th and 16th of the month.
  for (let dd = 11; dd <= 16; dd++) {
    const hd = new HDate(dd, hmonth, hyear);
    const zmanim = new Zmanim(location, hd, useElevation);
    if (zmanim.getSofZmanKidushLevanaBetweenMoldos() === null) {
      continue;
    }
    // Move a daytime time back to alot hashachar that morning
    // Same tzeit (8.5°) as isAssurBemlacha()
    const alos = toZdt(zmanim.alotHaShachar(), tzid);
    const tzeit = toZdt(zmanim.tzeit(), tzid);
    const zdt = zmanim.getSofZmanKidushLevanaBetweenMoldos(alos, tzeit);
    if (zdt === null) {
      return undefined;
    }
    const molad = new Molad(hyear, hmonth);
    // After daytime adjustment the time is at night: before alot hashachar
    // it belongs to today's Hebrew date, after tzeit to tomorrow's.
    const nightOf = zdt.hour < 12 ? hd : hd.next();
    if (!isShabbatOrChag(nightOf, il)) {
      return new KiddushLevanaEvent(
        hd,
        toDate(floorMinute(zdt)),
        location,
        molad,
        options
      );
    }
    // Back up to candle-lighting time before the Shabbat or Yom Tov
    let first = nightOf;
    while (isShabbatOrChag(first.prev(), il)) {
      first = first.prev();
    }
    const erev = first.prev();
    const erevZmanim = new Zmanim(location, erev, useElevation);
    const candleLighting = erevZmanim.sunsetOffset(
      Number(options.candleLightingMins),
      true
    );
    if (isNaN(candleLighting.getTime())) {
      return undefined;
    }
    return new KiddushLevanaEvent(erev, candleLighting, location, molad, options);
  }
  return undefined;
}
